import { useState, useEffect, useCallback, useMemo } from "react";
import { ChurnReview } from "./ChurnReview";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Separator } from "./components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "./components/ui/dropdown-menu";
import {
  Plus, Trash2, FileText, Check, Pencil,
  LogOut, BarChart3, LayoutDashboard, Search, ArrowUpDown, ArrowUp, ArrowDown, X, Users, ChevronDown, Calendar,
  Settings, UserPlus, Users2, Shield, ClipboardList, Heart
, AlertTriangle } from "lucide-react";
import { CardHeader, CardTitle } from "./components/ui/card";
import { PERIOD_PRESETS, getDateRange } from "./periodUtils";
import SalesDashboard from "./SalesDashboard";
import TrackerTab from "./TrackerTab";
import CSMPage from "./CSMPage";
import { NotificationsBell } from "./NotificationsBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtC = (v) => {
  const abs = Math.abs(v || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}€${abs.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const STATUS_COLORS = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  frozen: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  refunded: "bg-purple-50 text-purple-700 border-purple-200",
};
const STATUS_LABELS = { active: "Attivo", frozen: "Congelato", cancelled: "Annullato", refunded: "Rimborsato" };

const SECTORS = [
  "Benessere e fitness", "Commercio al dettaglio", "Edilizia",
  "Energia e attività estrattiva", "Finanza", "Formazione",
  "Immobiliare", "Legale", "Sanità", "Servizi aziendali",
  "Software e servizi IT", "Svago e viaggi", "Altro"
];

const REGIONS = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia",
  "Toscana", "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
  "Fuori Italia"
];

const COMPANY_SIZES = ["0-2M", "2M-10M", "10M-50M", "50M-250M", "250M+"];

const STATUS_REASONS = [
  "Budget ridotto", "Passaggio a competitor", "Insoddisfazione prodotto",
  "Ristrutturazione aziendale", "Cambio priorità", "Altro"
];

// =============== CLIENT DIALOG ===============
const ClientDialog = ({ open, onClose, client, onSave }) => {
  const isNew = !client;
  const [form, setForm] = useState({ name: "", sector: "", company_size: "", region: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({ name: client.name || "", sector: client.sector || "", company_size: client.company_size || "", region: client.region || "" });
    } else {
      setForm({ name: "", sector: "", company_size: "", region: "" });
    }
  }, [client, open]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Nome obbligatorio"); return; }
    if (!form.sector) { toast.error("Settore obbligatorio"); return; }
    if (!form.company_size) { toast.error("Dimensione obbligatoria"); return; }
    if (!form.region) { toast.error("Regione obbligatoria"); return; }
    setSaving(true);
    await onSave(form, client?.client_id);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="client-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nuovo Cliente" : `Modifica — ${client?.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div className="space-y-1">
            <Label className="text-xs">Nome Azienda *</Label>
            <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Es: Acme S.r.l." data-testid="client-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Settore *</Label>
            <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={form.sector} onChange={(e) => setForm(f => ({ ...f, sector: e.target.value }))} data-testid="client-sector">
              <option value="">Seleziona...</option>
              {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Dimensione Azienda (Fatturato) *</Label>
            <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={form.company_size} onChange={(e) => setForm(f => ({ ...f, company_size: e.target.value }))} data-testid="client-company-size">
              <option value="">Seleziona...</option>
              {COMPANY_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Regione *</Label>
            <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={form.region} onChange={(e) => setForm(f => ({ ...f, region: e.target.value }))} data-testid="client-region">
              <option value="">Seleziona...</option>
              {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="submit-client">
              <Check className="h-4 w-4" /> {isNew ? "Crea Cliente" : "Salva"}
            </Button>
            <Button variant="outline" onClick={() => onClose(false)}>Annulla</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============== CLIENTS TAB ===============
const ClientsTab = ({ clients, onRefresh, userRole }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [search, setSearch] = useState("");

  const handleSave = async (formData, clientId) => {
    try {
      if (clientId) {
        await axios.put(`${API}/clients/${clientId}`, formData);
        toast.success("Cliente aggiornato!");
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success("Cliente creato!");
      }
      setDialogOpen(false);
      setEditingClient(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Eliminare questo cliente?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success("Cliente eliminato");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const filtered = search.trim()
    ? clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.sector.toLowerCase().includes(search.toLowerCase()) || (c.region || "").toLowerCase().includes(search.toLowerCase()))
    : clients;

  return (
    <div className="space-y-4" data-testid="clients-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cerca cliente..." className="pl-9 h-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-clients" />
        </div>
        <div className="ml-auto">
          <Button onClick={() => { setEditingClient(null); setDialogOpen(true); }} className="gap-2 h-9" data-testid="new-client-btn">
            <Plus className="h-4 w-4" /> Nuovo Cliente
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="border-border/60">
          <CardContent className="p-12 text-center">
            <Users className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground">Nessun cliente trovato.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-border/60 overflow-hidden bg-background" data-testid="clients-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground uppercase tracking-wide">
                <th className="px-3 py-2.5 font-medium text-left">Azienda</th>
                <th className="px-3 py-2.5 font-medium text-left">Settore</th>
                <th className="px-3 py-2.5 font-medium text-left">Dimensione</th>
                <th className="px-3 py-2.5 font-medium text-left">Regione</th>
                <th className="px-3 py-2.5 font-medium text-right">Data Creazione</th>
                <th className="px-3 py-2.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.client_id} className="border-t border-border/40 hover:bg-muted/20 transition-colors" data-testid={`client-row-${c.client_id}`}>
                  <td className="px-3 py-3 font-medium">{c.name}</td>
                  <td className="px-3 py-3 text-muted-foreground">{c.sector}</td>
                  <td className="px-3 py-3">
                    <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100">{c.company_size}</span>
                  </td>
                  <td className="px-3 py-3 text-muted-foreground">{c.region}</td>
                  <td className="px-3 py-3 text-right text-muted-foreground text-xs">{new Date(c.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="px-2 py-3 flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingClient(c); setDialogOpen(true); }} data-testid={`edit-client-${c.client_id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {userRole === "admin" && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => handleDelete(c.client_id)} data-testid={`delete-client-${c.client_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-muted/30 border-t border-border/40 text-xs text-muted-foreground">
            {filtered.length} client{filtered.length === 1 ? "e" : "i"}
          </div>
        </div>
      )}

      <ClientDialog open={dialogOpen} onClose={(v) => { setDialogOpen(v); if (!v) setEditingClient(null); }} client={editingClient} onSave={handleSave} />
    </div>
  );
};

// =============== SEARCHABLE CLIENT SELECT ===============
const ClientSearchSelect = ({ clients, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = clients.find(c => c.client_id === value);
  const filtered = clients.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || (c.sector || "").toLowerCase().includes(q);
  });

  return (
    <div className="relative">
      <button
        type="button"
        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm"
        onClick={() => setOpen(!open)}
        data-testid="contract-client-select"
      >
        <span className={selected ? "" : "text-muted-foreground"}>
          {selected ? `${selected.name} — ${selected.sector}` : "Seleziona cliente..."}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-lg max-h-60 overflow-hidden">
          <div className="p-1.5 border-b border-border/40">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                className="w-full h-8 pl-7 pr-2 text-sm rounded border border-input bg-background"
                placeholder="Cerca cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                data-testid="client-search-input"
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground text-center">Nessun risultato</div>
            ) : (
              filtered.map(c => (
                <button
                  key={c.client_id}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 ${c.client_id === value ? "bg-muted" : ""}`}
                  onClick={() => { onChange(c.client_id); setOpen(false); setSearch(""); }}
                  data-testid={`client-option-${c.client_id}`}
                >
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">{c.sector} · {c.region}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============== EFFECTIVE DATE INLINE CELL ===============
const EffectiveDateCell = ({ contract, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(contract.status_change_date || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!value) return;
    setSaving(true);
    try {
      await axios.put(`${API}/contracts/${contract.contract_id}/effective-date`, { status_change_date: value });
      toast.success("Data termine aggiornata");
      setEditing(false);
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input type="date" className="h-7 w-[120px] text-xs" value={value} onChange={(e) => setValue(e.target.value)} data-testid={`effective-date-input-${contract.contract_id}`} />
        <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={save} disabled={saving} data-testid={`effective-date-save-${contract.contract_id}`}>
          <Check className="h-3 w-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-muted-foreground" onClick={() => setEditing(false)}>
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  const display = contract.status_change_date
    ? new Date(contract.status_change_date).toLocaleDateString("it-IT")
    : "—";
  const isMissing = !contract.status_change_date || contract.status_change_date === contract.created_at?.slice(0, 10);

  return (
    <button
      className={`text-xs px-1.5 py-0.5 rounded cursor-pointer hover:bg-muted transition-colors ${isMissing ? "text-orange-600 bg-orange-50 border border-orange-200" : "text-muted-foreground"}`}
      onClick={() => { setValue(contract.status_change_date || ""); setEditing(true); }}
      title="Clicca per modificare la data di termine"
      data-testid={`effective-date-${contract.contract_id}`}
    >
      {display} {isMissing && "⚠"}
    </button>
  );
};


// =============== CONTRACT DIALOG (with client search, date, owner) ===============
const ContractDialog = ({ open, onClose, products, clients, salesReps, contract, onSave, onStatusChange, onDelete, userRole }) => {
  const isNew = !contract;
  const [form, setForm] = useState({
    client_id: "", sale_type: "New", notes: "", contract_date: "", sales_rep_id: "", expiration_date: "",
    line_items: [{ product_id: "", product_name: "", quantity: 1, unit_price: "", list_price: 0, months: 12 }]
  });
  const [saving, setSaving] = useState(false);
  const [statusSection, setStatusSection] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");

  useEffect(() => {
    if (contract) {
      const dateStr = contract.created_at ? contract.created_at.split("T")[0] : "";
      setForm({
        client_id: contract.client_id || "",
        sale_type: contract.sale_type || "New",
        notes: contract.notes || "",
        contract_date: dateStr,
        sales_rep_id: contract.sales_rep_id || "",
        expiration_date: contract.expiration_date || "",
        line_items: (contract.line_items || []).map(li => ({
          product_id: li.product_id || "",
          product_name: li.product_name || "",
          quantity: li.quantity || 1,
          unit_price: String(li.unit_price ?? 0),
          list_price: li.list_price || 0,
          months: li.months || 12,
        }))
      });
      setNewStatus(contract.status || "active");
    } else {
      setForm({
        client_id: "", sale_type: "New", notes: "", contract_date: new Date().toISOString().split("T")[0], sales_rep_id: "", expiration_date: "",
        line_items: [{ product_id: "", product_name: "", quantity: 1, unit_price: "", list_price: 0, months: 12 }]
      });
    }
    setStatusSection(false);
    setStatusReason("");
    setStatusNote("");
    setEffectiveDate(new Date().toISOString().split("T")[0]);
  }, [contract, open]);

  const updateLineItem = (index, field, value) => {
    const items = [...form.line_items];
    items[index] = { ...items[index], [field]: value };
    if (field === "product_id") {
      const prod = products.find(p => p.product_id === value);
      if (prod) {
        items[index].product_name = prod.name;
        items[index].list_price = prod.monthly_price;
        items[index].unit_price = String(prod.monthly_price);
      }
    }
    setForm(f => ({ ...f, line_items: items }));
  };

  const addLineItem = () => {
    setForm(f => ({ ...f, line_items: [...f.line_items, { product_id: "", product_name: "", quantity: 1, unit_price: "", list_price: 0, months: 12 }] }));
  };

  const removeLineItem = (index) => {
    if (form.line_items.length <= 1) return;
    setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== index) }));
  };

  const parsedLineItems = form.line_items.map(li => ({ ...li, _price: parseFloat(li.unit_price) || 0 }));
  const totalContracted = parsedLineItems.reduce((s, li) => s + (li._price * li.quantity * li.months), 0);
  const mrr = parsedLineItems.reduce((s, li) => s + (li._price * li.quantity), 0);
  const arr = mrr * 12;

  const selectedClient = clients.find(c => c.client_id === form.client_id);

  const handleSave = async () => {
    if (!form.client_id) { toast.error("Seleziona un cliente"); return; }
    for (const li of form.line_items) {
      if (!li.product_id) { toast.error("Seleziona un prodotto per ogni riga"); return; }
      if (li.quantity < 1) { toast.error("Quantità min. 1"); return; }
    }
    const payload = {
      client_id: form.client_id,
      sale_type: form.sale_type,
      notes: form.notes,
      contract_date: form.contract_date || "",
      expiration_date: form.expiration_date || "",
      sales_rep_id: form.sales_rep_id || "",
      line_items: form.line_items.map(li => ({
        product_id: li.product_id,
        product_name: li.product_name,
        quantity: li.quantity,
        unit_price: parseFloat(li.unit_price) || 0,
        list_price: li.list_price,
        months: li.months
      }))
    };
    setSaving(true);
    await onSave(payload, contract?.contract_id);
    setSaving(false);
  };

  const handleStatusSave = async () => {
    if (newStatus !== "active" && !statusReason) { toast.error("Motivo obbligatorio"); return; }
    setSaving(true);
    await onStatusChange(contract.contract_id, newStatus, statusReason, statusNote, effectiveDate);
    setSaving(false);
    setStatusSection(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nuovo Contratto" : `Modifica — ${contract?.client_name}`}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-1">
          {/* Client (searchable) + Sale type */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Cliente *</Label>
              <ClientSearchSelect clients={clients} value={form.client_id} onChange={(id) => setForm(f => ({ ...f, client_id: id }))} />
              {selectedClient && (
                <div className="flex gap-2 mt-1 flex-wrap">
                  <span className="text-[11px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground">{selectedClient.sector}</span>
                  <span className="text-[11px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground">{selectedClient.company_size}</span>
                  <span className="text-[11px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground">{selectedClient.region}</span>
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tipo Vendita</Label>
              <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={form.sale_type} onChange={(e) => setForm(f => ({ ...f, sale_type: e.target.value }))} data-testid="contract-sale-type">
                <option value="New">New</option>
                <option value="Up-Sell">Up-Sell</option>
              </select>
            </div>
          </div>

          {/* Contract Date + Sales Rep Owner */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Data Contratto</Label>
              <Input type="date" value={form.contract_date} onChange={(e) => setForm(f => ({ ...f, contract_date: e.target.value }))} data-testid="contract-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Scadenza Contratto</Label>
              <Input type="date" value={form.expiration_date} onChange={(e) => setForm(f => ({ ...f, expiration_date: e.target.value }))} data-testid="contract-expiration" />
            </div>
            {userRole === "admin" && (
              <div className="space-y-1">
                <Label className="text-xs">Proprietario (Sales Rep)</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={form.sales_rep_id} onChange={(e) => setForm(f => ({ ...f, sales_rep_id: e.target.value }))} data-testid="contract-owner-select">
                  <option value="">{isNew ? "Assegna a me" : (contract?.sales_rep_name || "Non assegnato")}</option>
                  {salesReps.map(r => <option key={r.user_id} value={r.user_id}>{r.name || r.email}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Products */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Prodotti</Label>
              <Button variant="outline" size="sm" onClick={addLineItem} className="gap-1 h-7 text-xs" data-testid="add-line-item">
                <Plus className="h-3 w-3" /> Aggiungi
              </Button>
            </div>

            <div className="rounded-md border border-border/60 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-xs text-muted-foreground">
                    <th className="text-left px-3 py-2 font-medium">Prodotto</th>
                    <th className="text-right px-2 py-2 font-medium w-16">Lic.</th>
                    <th className="text-right px-2 py-2 font-medium w-24">Prezzo/mese</th>
                    <th className="text-right px-2 py-2 font-medium w-16">Mesi</th>
                    <th className="text-right px-2 py-2 font-medium w-20">Sconto</th>
                    <th className="text-right px-2 py-2 font-medium w-24">Totale</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.line_items.map((li, i) => {
                    const price = parseFloat(li.unit_price) || 0;
                    const discountPct = li.list_price > 0 && price < li.list_price
                      ? ((1 - price / li.list_price) * 100).toFixed(1) : null;
                    const lineTotal = price * li.quantity * li.months;
                    return (
                      <tr key={i} className="border-t border-border/40" data-testid={`line-item-${i}`}>
                        <td className="px-3 py-2">
                          <select className="h-8 w-full rounded border border-input bg-background px-2 text-sm" value={li.product_id} onChange={(e) => updateLineItem(i, "product_id", e.target.value)} data-testid={`line-product-${i}`}>
                            <option value="">Seleziona...</option>
                            {products.map(p => <option key={p.product_id} value={p.product_id}>{p.name} ({fmtC(p.monthly_price)}/m)</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <Input type="number" min="1" className="h-8 text-right" value={li.quantity} onChange={(e) => updateLineItem(i, "quantity", parseInt(e.target.value) || 1)} data-testid={`line-qty-${i}`} />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="text"
                            inputMode="decimal"
                            className="h-8 text-right"
                            value={li.unit_price}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "" || /^\d*\.?\d*$/.test(v)) {
                                updateLineItem(i, "unit_price", v);
                              }
                            }}
                            onBlur={(e) => {
                              const n = parseFloat(e.target.value);
                              if (!isNaN(n)) updateLineItem(i, "unit_price", String(n));
                              else if (e.target.value === "") updateLineItem(i, "unit_price", "0");
                            }}
                            data-testid={`line-price-${i}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input type="number" min="1" className="h-8 text-right" value={li.months} onChange={(e) => updateLineItem(i, "months", parseInt(e.target.value) || 12)} data-testid={`line-months-${i}`} />
                        </td>
                        <td className="px-2 py-2 text-right">
                          {discountPct && parseFloat(discountPct) > 0 ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-xs font-medium" data-testid={`line-discount-${i}`}>-{discountPct}%</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right font-medium text-sm">{fmtC(lineTotal)}</td>
                        <td className="px-1 py-2">
                          {form.line_items.length > 1 && (
                            <button onClick={() => removeLineItem(i)} className="text-red-400 hover:text-red-600 p-1" data-testid={`remove-line-${i}`}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-3 gap-3 p-3 bg-muted/40 rounded-lg text-center">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Contrattualizzato</p>
              <p className="text-base font-bold">{fmtC(totalContracted)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">MRR</p>
              <p className="text-base font-bold">{fmtC(mrr)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">ARR</p>
              <p className="text-base font-bold text-emerald-600">{fmtC(arr)}</p>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label className="text-xs">Note</Label>
            <textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Link CRM, info aggiuntive..." data-testid="contract-notes" />
          </div>

          {/* Status Change (edit mode only) */}
          {!isNew && (
            <>
              <Separator />
              {!statusSection ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Stato attuale:</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[contract.status]}`}>{STATUS_LABELS[contract.status]}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setStatusSection(true)} data-testid="open-status-change">Cambia Stato</Button>
                </div>
              ) : (
                <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nuovo Stato</Label>
                      <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="new-status-select">
                        <option value="active">Attivo</option>
                        <option value="frozen">Congelato</option>
                        <option value="cancelled">Annullato</option>
                      </select>
                    </div>
                    {newStatus !== "active" && (
                      <div className="space-y-1">
                        <Label className="text-xs">Motivo *</Label>
                        <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={statusReason} onChange={(e) => setStatusReason(e.target.value)} data-testid="status-reason-select">
                          <option value="">Seleziona...</option>
                          {STATUS_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    )}
                    {newStatus !== "active" && (
                      <div className="space-y-1">
                        <Label className="text-xs">Data Effettiva Termine</Label>
                        <Input type="date" className="h-9 text-sm" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="status-effective-date" />
                        <p className="text-[10px] text-muted-foreground">Data a cui imputare la perdita del contratto</p>
                      </div>
                    )}
                    {newStatus !== "active" && (
                      <div className="space-y-1">
                        <Label className="text-xs">Note (opzionale)</Label>
                        <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[50px] placeholder:text-muted-foreground" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Dettagli aggiuntivi..." data-testid="status-note" />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs" onClick={handleStatusSave} disabled={saving} data-testid="confirm-status-change">Conferma</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setStatusSection(false)}>Annulla</Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="submit-contract">
              <Check className="h-4 w-4" /> {isNew ? "Crea Contratto" : "Salva Modifiche"}
            </Button>
            <Button variant="outline" onClick={() => onClose(false)}>Annulla</Button>
            {!isNew && userRole === "admin" && (
              <Button variant="ghost" size="sm" className="text-red-500 ml-auto gap-1" onClick={() => { onDelete(contract.contract_id); onClose(false); }} data-testid={`delete-contract-${contract?.contract_id}`}>
                <Trash2 className="h-3.5 w-3.5" /> Elimina
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============== SORTABLE COLUMN HEADER ===============
const SortHeader = ({ label, field, sort, setSort, className = "" }) => {
  const active = sort.field === field;
  const toggle = () => {
    if (active) setSort({ field, dir: sort.dir === "asc" ? "desc" : "asc" });
    else setSort({ field, dir: "desc" });
  };
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={`px-3 py-2.5 font-medium cursor-pointer select-none hover:bg-muted/60 transition-colors ${className}`} onClick={toggle} data-testid={`sort-${field}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`h-3 w-3 ${active ? "text-foreground" : "text-muted-foreground/50"}`} />
      </span>
    </th>
  );
};

// =============== MAIN CONTRACTS PAGE ===============
export default function ContractsPage({ initialView }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isLoading, setIsLoading] = useState(!location.state?.user);
  const [contracts, setContracts] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const initFocus = location.state?.focus;
  const [activeView, setActiveView] = useState(initFocus ? (initFocus.type === "red_alert" ? "csm" : "tracker") : (initialView || "contracts"));
  const [focus, setFocus] = useState(initFocus ? { ...initFocus, n: Date.now() } : null);
  const openNotification = (n) => {
    const isClient = n.type === "red_alert";
    setActiveView(isClient ? "csm" : "tracker");
    setFocus({ type: n.type, id: isClient ? n.client_id : n.item_id, n: Date.now() });
  };

  // Filters & search
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [salesRepFilter, setSalesRepFilter] = useState("all");
  const [periodPreset, setPeriodPreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [sort, setSort] = useState({ field: "created_at", dir: "desc" });
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Contract dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);

  // Settings state (admin)
  const [settingsSection, setSettingsSection] = useState("users");
  const [managedUsers, setManagedUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: "", password: "", name: "", role: "sales" });
  const [creatingUser, setCreatingUser] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", monthly_price: "" });
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);

  // Auth check
  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsLoading(false);
      return;
    }
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (e) {
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  const loadData = useCallback(async () => {
    try {
      const [cRes, pRes, clRes, srRes] = await Promise.all([
        axios.get(`${API}/contracts`).catch(() => ({ data: { contracts: [] } })),
        axios.get(`${API}/products`).catch(() => ({ data: { products: [] } })),
        axios.get(`${API}/clients`).catch(() => ({ data: { clients: [] } })),
        axios.get(`${API}/meta/sales-reps`).catch(() => ({ data: { reps: [] } }))
      ]);
      setContracts(cRes.data.contracts || []);
      setProducts(pRes.data.products || []);
      setClients(clRes.data.clients || []);
      setSalesReps(srRes.data.reps || []);
    } catch (e) { toast.error("Errore nel caricamento"); }
    setDataLoading(false);
  }, []);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  const handleSave = async (formData, contractId) => {
    try {
      if (contractId) {
        await axios.put(`${API}/contracts/${contractId}`, formData);
        toast.success("Contratto aggiornato!");
      } else {
        await axios.post(`${API}/contracts`, formData);
        toast.success("Contratto creato!");
      }
      setDialogOpen(false);
      setEditingContract(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const handleStatusChange = async (contractId, status, reason, note, effectiveDate) => {
    try {
      await axios.put(`${API}/contracts/${contractId}/status`, { status, status_reason: reason, status_note: note || "", effective_date: effectiveDate || "" });
      toast.success("Stato aggiornato");
      loadData();
      setDialogOpen(false);
      setEditingContract(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm("Eliminare questo contratto?")) return;
    try {
      await axios.delete(`${API}/contracts/${contractId}`);
      toast.success("Contratto eliminato");
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* */ }
    toast.success("Logout effettuato");
    navigate('/login');
  };

  // ===== User Management (Admin only) =====
  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setManagedUsers(response.data.users || []);
    } catch (e) { /* ignore for non-admin */ }
  };

  const createNewUser = async () => {
    if (!newUserForm.email || !newUserForm.password || !newUserForm.name) {
      toast.error("Compila tutti i campi"); return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, newUserForm);
      toast.success("Utente creato!");
      setNewUserForm({ email: "", password: "", name: "", role: "sales" });
      setShowCreateUser(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    }
    setCreatingUser(false);
  };

  const deleteUserById = async (userId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo utente?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Utente eliminato");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole });
      toast.success("Ruolo aggiornato");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  // ===== Product Management (Admin only) =====
  const loadSettingsProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.products || []);
    } catch (e) { /* ignore */ }
  };

  const createProduct = async () => {
    if (!newProduct.name.trim() || !newProduct.monthly_price) {
      toast.error("Inserisci nome e prezzo"); return;
    }
    setSavingProduct(true);
    try {
      await axios.post(`${API}/products`, { name: newProduct.name, monthly_price: parseFloat(newProduct.monthly_price) });
      toast.success("Prodotto creato!");
      setNewProduct({ name: "", monthly_price: "" });
      setShowCreateProduct(false);
      loadSettingsProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    }
    setSavingProduct(false);
  };

  const saveEditProduct = async () => {
    if (!editingProduct) return;
    setSavingProduct(true);
    try {
      await axios.put(`${API}/products/${editingProduct.product_id}`, {
        name: editingProduct.name,
        monthly_price: parseFloat(editingProduct.monthly_price)
      });
      toast.success("Prodotto aggiornato!");
      setEditingProduct(null);
      loadSettingsProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiornamento");
    }
    setSavingProduct(false);
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo prodotto?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Prodotto eliminato");
      loadSettingsProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  useEffect(() => {
    if ((user?.role === 'admin' || user?.role === 'csm') && activeView === 'settings') {
      loadUsers();
      loadSettingsProducts();
    }
  }, [activeView, user?.role]);

  const sectors = useMemo(() => [...new Set(contracts.map(c => c.sector).filter(Boolean))].sort(), [contracts]);

  const filtered = useMemo(() => {
    let data = [...contracts];
    if (statusFilter !== "all") data = data.filter(c => c.status === statusFilter);
    if (sectorFilter !== "all") data = data.filter(c => c.sector === sectorFilter);
    if (salesRepFilter !== "all") data = data.filter(c => c.sales_rep_id === salesRepFilter);
    
    // Period filter
    const range = periodPreset === "custom" ? { from: customFrom, to: customTo } : getDateRange(periodPreset);
    if (range.from) data = data.filter(c => (c.created_at || "") >= range.from);
    if (range.to) data = data.filter(c => (c.created_at || "").slice(0, 10) <= range.to);
    
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(c =>
        c.client_name?.toLowerCase().includes(q) ||
        c.sector?.toLowerCase().includes(q) ||
        c.sales_rep_name?.toLowerCase().includes(q) ||
        c.notes?.toLowerCase().includes(q)
      );
    }
    data.sort((a, b) => {
      if (sort.field === "end_date") {
        const ae = a.status !== "active" ? (a.status_change_date || "") : "", be = b.status !== "active" ? (b.status_change_date || "") : "";
        if (!ae || !be) return (ae ? 0 : 1) - (be ? 0 : 1);
        return sort.dir === "asc" ? ae.localeCompare(be) : be.localeCompare(ae);
      }
      let av = a[sort.field], bv = b[sort.field];
      if (sort.field === "created_at") { av = av || ""; bv = bv || ""; }
      if (typeof av === "string") {
        const cmp = (av || "").localeCompare(bv || "", "it");
        return sort.dir === "asc" ? cmp : -cmp;
      }
      return sort.dir === "asc" ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return data;
  }, [contracts, statusFilter, sectorFilter, salesRepFilter, periodPreset, customFrom, customTo, search, sort]);

  const activeContracts = filtered.filter(c => c.status === "active");
  const totalArr = activeContracts.reduce((s, c) => s + (c.net_arr || c.arr || 0), 0);
  const totalContracted = activeContracts.reduce((s, c) => s + (c.net_contracted || c.total_contracted || 0), 0);
  const totalMrr = activeContracts.reduce((s, c) => s + (c.mrr || 0), 0);

  const uniqueReps = useMemo(() => {
    const map = {};
    contracts.forEach(c => { if (c.sales_rep_id && c.sales_rep_name) map[c.sales_rep_id] = c.sales_rep_name; });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [contracts]);

  const hasActiveFilters = search || statusFilter !== "all" || sectorFilter !== "all" || salesRepFilter !== "all" || periodPreset !== "all";

  const getDiscountSummary = (c) => {
    if (!c.line_items?.length) return null;
    const discounts = c.line_items.filter(li => li.list_price > 0 && li.unit_price < li.list_price);
    if (!discounts.length) return null;
    const avgDiscount = discounts.reduce((s, li) => s + (1 - li.unit_price / li.list_price), 0) / discounts.length * 100;
    return avgDiscount.toFixed(0);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="contracts-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header border-b border-border/60">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4">
                <img src="/api/static/logo" alt="Aiclo" className="h-9" />
                <h1 className="text-xl font-bold tracking-tight">Sales CRM</h1>
              </div>
              <div className="flex bg-muted rounded-md p-0.5" data-testid="crm-view-tabs">
                <button onClick={() => setActiveView("dashboard")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "dashboard" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="view-tab-dashboard">
                    <LayoutDashboard className="h-3.5 w-3.5" />Dashboard
                  </button>
                <button onClick={() => setActiveView("contracts")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "contracts" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="view-tab-contracts">
                  <FileText className="h-3.5 w-3.5" />Contratti
                </button>
                <button onClick={() => setActiveView("clients")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "clients" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="view-tab-clients">
                  <Users className="h-3.5 w-3.5" />Clienti
                </button>
                {(user?.role === "admin" || user?.role === "csm") && (
                  <button onClick={() => setActiveView("tracker")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "tracker" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="view-tab-tracker">
                    <ClipboardList className="h-3.5 w-3.5" />Tracker
                  </button>
                )}
                {(user?.role === "admin" || user?.role === "csm") && (
                  <button onClick={() => setActiveView("csm")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "csm" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="view-tab-csm">
                    <Heart className="h-3.5 w-3.5" />CSM
                  </button>
                )}
                {(user?.role === "admin" || user?.role === "csm") && (
                  <button onClick={() => setActiveView("settings")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "settings" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="view-tab-settings">
                    <Settings className="h-3.5 w-3.5" />Impostazioni
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationsBell onOpen={openNotification} />
              {user?.role === "admin" && (
                <Button variant="outline" className="gap-2 h-9" onClick={() => navigate('/dashboard')} data-testid="goto-planner-btn">
                  <BarChart3 className="h-4 w-4" />Financial Planner
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2" data-testid="contracts-user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium hidden md:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <p className="text-xs text-muted-foreground capitalize mt-0.5">Ruolo: {user?.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="contracts-logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-8 space-y-6">
        {activeView === "dashboard" ? (
          <SalesDashboard userRole={user?.role} />
        ) : activeView === "clients" ? (
          <ClientsTab clients={clients} onRefresh={loadData} userRole={user?.role} />
        ) : (user?.role === "admin" || user?.role === "csm") && activeView === "tracker" ? (
          <TrackerTab userRole={user?.role} clients={clients} focusItem={focus?.type !== "red_alert" ? focus : null} />
        ) : (user?.role === "admin" || user?.role === "csm") && activeView === "csm" ? (
          <CSMPage focusClient={focus?.type === "red_alert" ? focus : null} />
        ) : (user?.role === "admin" || user?.role === "csm") && activeView === "settings" ? (
          <div className="space-y-6">
            {/* Sub-navigation */}
            <div className="flex items-center gap-2 border-b border-border pb-3">
              {[
                { key: "users", label: "Utenti", icon: Users2 },
                { key: "products", label: "Prodotti", icon: FileText },
                ...(user?.role === "admin" ? [{ key: "churn", label: "Verifica Churn", icon: AlertTriangle }] : [])
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setSettingsSection(key)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    settingsSection === key
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                  data-testid={`settings-section-${key}`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {settingsSection === "churn" && user?.role === "admin" && <ChurnReview />}

            {/* ===== Users Section ===== */}
            {settingsSection === "users" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Gestione Utenti</h2>
                    <p className="text-sm text-muted-foreground mt-1">Crea e gestisci gli account del team</p>
                  </div>
                  {user?.role === "admin" && (
                  <Button onClick={() => setShowCreateUser(!showCreateUser)} className="gap-2" data-testid="create-user-btn">
                    <UserPlus className="h-4 w-4" />
                    Nuovo Utente
                  </Button>
                  )}
                </div>

                {showCreateUser && user?.role === "admin" && (
                  <Card className="border-primary/20" data-testid="create-user-form">
                    <CardHeader><CardTitle className="text-lg">Crea Nuovo Utente</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label>Nome</Label>
                          <Input placeholder="Nome Cognome" value={newUserForm.name} onChange={(e) => setNewUserForm(p => ({ ...p, name: e.target.value }))} data-testid="new-user-name" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Email</Label>
                          <Input type="email" placeholder="email@azienda.com" value={newUserForm.email} onChange={(e) => setNewUserForm(p => ({ ...p, email: e.target.value }))} data-testid="new-user-email" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Password</Label>
                          <Input type="password" placeholder="Password" value={newUserForm.password} onChange={(e) => setNewUserForm(p => ({ ...p, password: e.target.value }))} data-testid="new-user-password" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Ruolo</Label>
                          <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={newUserForm.role} onChange={(e) => setNewUserForm(p => ({ ...p, role: e.target.value }))} data-testid="new-user-role">
                            <option value="sales">Sales</option>
                            <option value="csm">CSM</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button onClick={createNewUser} disabled={creatingUser} className="gap-2" data-testid="submit-create-user">
                          {creatingUser ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" /> : <UserPlus className="h-4 w-4" />}
                          Crea Utente
                        </Button>
                        <Button variant="outline" onClick={() => setShowCreateUser(false)}>Annulla</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="border-border/60">
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/50">
                          <th className="text-left p-4 font-medium text-muted-foreground">Nome</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Email</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Ruolo</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Data</th>
                          {user?.role === "admin" && <th className="text-right p-4 font-medium text-muted-foreground">Azioni</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {managedUsers.map((u) => (
                          <tr key={u.user_id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`user-row-${u.user_id}`}>
                            <td className="p-4 font-medium">{u.name}</td>
                            <td className="p-4 text-muted-foreground">{u.email}</td>
                            <td className="p-4">
                              {u.user_id !== user.user_id && user?.role === "admin" ? (
                                <select
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer appearance-none bg-transparent"
                                  style={{ color: u.role === 'admin' ? 'hsl(var(--primary))' : u.role === 'csm' ? '#7c3aed' : '#1d4ed8', backgroundColor: u.role === 'admin' ? 'hsl(var(--primary) / 0.1)' : u.role === 'csm' ? '#f5f3ff' : '#eff6ff' }}
                                  value={u.role}
                                  onChange={(e) => updateUserRole(u.user_id, e.target.value)}
                                  data-testid={`role-select-${u.user_id}`}
                                >
                                  <option value="admin">Admin</option>
                                  <option value="sales">Sales</option>
                                  <option value="csm">CSM</option>
                                </select>
                              ) : (
                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-primary/10 text-primary' : u.role === 'csm' ? 'bg-violet-50 text-violet-700' : 'bg-blue-50 text-blue-700'}`}>
                                  <Shield className="h-3 w-3" />{u.role === 'admin' ? (u.user_id === user.user_id ? 'Admin (tu)' : 'Admin') : u.role === 'csm' ? 'CSM' : 'Sales'}
                                </span>
                              )}
                            </td>
                            <td className="p-4 text-muted-foreground text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT') : '-'}</td>
                            <td className="p-4 text-right">
                              {u.user_id !== user.user_id && user?.role === "admin" && (
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteUserById(u.user_id)} data-testid={`delete-user-${u.user_id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ===== Products Section ===== */}
            {settingsSection === "products" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Catalogo Prodotti</h2>
                    <p className="text-sm text-muted-foreground mt-1">Gestisci i prodotti e i listini prezzo</p>
                  </div>
                  {user?.role === "admin" && (
                  <Button onClick={() => { setShowCreateProduct(!showCreateProduct); setEditingProduct(null); }} className="gap-2" data-testid="create-product-btn">
                    <Plus className="h-4 w-4" />
                    Nuovo Prodotto
                  </Button>
                  )}
                </div>

                {showCreateProduct && user?.role === "admin" && (
                  <Card className="border-primary/20" data-testid="create-product-form">
                    <CardHeader><CardTitle className="text-lg">Nuovo Prodotto</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label>Nome Prodotto</Label>
                          <Input placeholder="es. Piano Enterprise" value={newProduct.name} onChange={(e) => setNewProduct(p => ({ ...p, name: e.target.value }))} data-testid="new-product-name" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Prezzo Mensile per Licenza (€)</Label>
                          <Input type="text" inputMode="decimal" placeholder="0.00" value={newProduct.monthly_price} onChange={(e) => { const v = e.target.value; if (v === "" || /^\d*\.?\d*$/.test(v)) setNewProduct(p => ({ ...p, monthly_price: v })); }} data-testid="new-product-price" />
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button onClick={createProduct} disabled={savingProduct} className="gap-2" data-testid="submit-create-product">
                          {savingProduct ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" /> : <Plus className="h-4 w-4" />}
                          Crea Prodotto
                        </Button>
                        <Button variant="outline" onClick={() => setShowCreateProduct(false)}>Annulla</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="border-border/60">
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/50">
                          <th className="text-left p-4 font-medium text-muted-foreground">Prodotto</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Prezzo Mensile / Licenza</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Prezzo Annuale / Licenza</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Ultimo Aggiornamento</th>
                          {user?.role === "admin" && <th className="text-right p-4 font-medium text-muted-foreground">Azioni</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {products.length === 0 ? (
                          <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nessun prodotto. Crea il primo prodotto per iniziare.</td></tr>
                        ) : products.map((p) => (
                          <tr key={p.product_id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`product-row-${p.product_id}`}>
                            {editingProduct?.product_id === p.product_id && user?.role === "admin" ? (
                              <>
                                <td className="p-3">
                                  <Input value={editingProduct.name} onChange={(e) => setEditingProduct(prev => ({ ...prev, name: e.target.value }))} className="h-9" data-testid="edit-product-name" />
                                </td>
                                <td className="p-3">
                                  <Input type="text" inputMode="decimal" value={editingProduct.monthly_price} onChange={(e) => { const v = e.target.value; if (v === "" || /^\d*\.?\d*$/.test(v)) setEditingProduct(prev => ({ ...prev, monthly_price: v })); }} className="h-9" data-testid="edit-product-price" />
                                </td>
                                <td className="p-3 text-muted-foreground">€{(parseFloat(editingProduct.monthly_price || 0) * 12).toFixed(2)}</td>
                                <td className="p-3" />
                                <td className="p-3 text-right space-x-1">
                                  <Button size="sm" onClick={saveEditProduct} disabled={savingProduct} data-testid="save-edit-product">Salva</Button>
                                  <Button size="sm" variant="outline" onClick={() => setEditingProduct(null)}>Annulla</Button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="p-4 font-medium">{p.name}</td>
                                <td className="p-4">€{(p.monthly_price || 0).toFixed(2)}</td>
                                <td className="p-4 text-muted-foreground">€{((p.monthly_price || 0) * 12).toFixed(2)}</td>
                                <td className="p-4 text-muted-foreground text-xs">{p.updated_at ? new Date(p.updated_at).toLocaleDateString('it-IT') : '-'}</td>
                                {user?.role === "admin" && (
                                <td className="p-4 text-right space-x-1">
                                  <Button variant="ghost" size="sm" onClick={() => { setEditingProduct({ ...p }); setShowCreateProduct(false); }} data-testid={`edit-product-${p.product_id}`}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteProduct(p.product_id)} data-testid={`delete-product-${p.product_id}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </td>
                                )}
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-border/60">
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Contratti Attivi</p>
                  <p className="text-2xl font-bold mt-1" data-testid="kpi-active-count">{activeContracts.length}</p>
                </CardContent>
              </Card>
              <Card className="border-border/60">
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">MRR Attivo</p>
                  <p className="text-2xl font-bold mt-1" data-testid="kpi-mrr">{fmtC(totalMrr)}</p>
                </CardContent>
              </Card>
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">ARR Attivo</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="kpi-arr">{fmtC(totalArr)}</p>
                </CardContent>
              </Card>
              <Card className="border-border/60">
                <CardContent className="p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Contrattualizzato Netto</p>
                  <p className="text-2xl font-bold mt-1" data-testid="kpi-contracted">{fmtC(totalContracted)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Cerca..." className="pl-9 h-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-contracts" />
              </div>
              <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status">
                <option value="all">Tutti gli stati</option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)} data-testid="filter-sector">
                <option value="all">Tutti i settori</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={salesRepFilter} onChange={(e) => setSalesRepFilter(e.target.value)} data-testid="filter-sales-rep">
                <option value="all">Tutti i sales</option>
                {uniqueReps.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
              <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={periodPreset} onChange={(e) => setPeriodPreset(e.target.value)} data-testid="filter-period">
                {PERIOD_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              {periodPreset === "custom" && (
                <div className="flex items-center gap-1">
                  <Input type="date" className="h-9 w-[130px] text-sm" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="filter-date-from" />
                  <span className="text-xs text-muted-foreground">—</span>
                  <Input type="date" className="h-9 w-[130px] text-sm" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="filter-date-to" />
                </div>
              )}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="h-9 text-xs gap-1" onClick={() => { setSearch(""); setStatusFilter("all"); setSectorFilter("all"); setSalesRepFilter("all"); setPeriodPreset("all"); setCustomFrom(""); setCustomTo(""); }} data-testid="clear-filters">
                  <X className="h-3.5 w-3.5" /> Reset
                </Button>
              )}
              <div className="ml-auto">
                <Button onClick={() => { setEditingContract(null); setDialogOpen(true); }} className="gap-2 h-9" data-testid="new-contract-btn">
                  <Plus className="h-4 w-4" /> Nuovo Contratto
                </Button>
              </div>
            </div>

            {/* Table */}
            {dataLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : filtered.length === 0 ? (
              <Card className="border-border/60">
                <CardContent className="p-12 text-center">
                  <FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-muted-foreground">Nessun contratto trovato.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="rounded-lg border border-border/60 bg-background" data-testid="contracts-table">
                {(() => {
                  const sel = filtered.filter(c => selectedIds.has(c.contract_id));
                  if (sel.length === 0) return null;
                  const tot = (fn) => sel.reduce((a, c) => a + (fn(c) || 0), 0);
                  return (
                    <div className="sticky top-[69px] z-40 rounded-t-lg shadow-md flex items-center gap-6 flex-wrap px-4 py-2.5 bg-primary text-primary-foreground text-sm" data-testid="contracts-selection-bar">
                      <span className="font-semibold" data-testid="selection-count">{sel.length} selezionat{sel.length === 1 ? "o" : "i"}</span>
                      <span>Contrattualizzato <b data-testid="selection-total-contracted">{fmtC(tot(c => c.net_contracted || c.total_contracted))}</b></span>
                      <span>MRR <b data-testid="selection-total-mrr">{fmtC(tot(c => c.mrr))}</b></span>
                      <span>ARR <b data-testid="selection-total-arr">{fmtC(tot(c => c.net_arr || c.arr))}</b></span>
                      <button className="ml-auto text-xs underline opacity-90 hover:opacity-100" onClick={() => setSelectedIds(new Set())} data-testid="selection-clear">Deseleziona</button>
                    </div>
                  );
                })()}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 text-xs text-muted-foreground uppercase tracking-wide">
                      <th className="pl-3 py-2.5 w-8">
                        <input type="checkbox" className="h-4 w-4 cursor-pointer accent-primary" data-testid="select-all-contracts"
                          checked={filtered.length > 0 && filtered.every(c => selectedIds.has(c.contract_id))}
                          ref={el => { if (el) el.indeterminate = filtered.some(c => selectedIds.has(c.contract_id)) && !filtered.every(c => selectedIds.has(c.contract_id)); }}
                          onChange={(e) => setSelectedIds(prev => {
                            const next = new Set(prev);
                            filtered.forEach(c => e.target.checked ? next.add(c.contract_id) : next.delete(c.contract_id));
                            return next;
                          })} />
                      </th>
                      <SortHeader label="Cliente" field="client_name" sort={sort} setSort={setSort} className="text-left" />
                      <SortHeader label="Stato" field="status" sort={sort} setSort={setSort} className="text-left" />
                      <th className="px-3 py-2.5 font-medium text-left">Settore</th>
                      <th className="px-3 py-2.5 font-medium text-left">Tipo</th>
                      <th className="px-3 py-2.5 font-medium text-left">Prodotti</th>
                      <th className="px-3 py-2.5 font-medium text-right">Sconto</th>
                      <SortHeader label="MRR" field="mrr" sort={sort} setSort={setSort} className="text-right" />
                      <SortHeader label="ARR" field="arr" sort={sort} setSort={setSort} className="text-right" />
                      <SortHeader label="Valore" field="total_contracted" sort={sort} setSort={setSort} className="text-right" />
                      <SortHeader label="Data" field="created_at" sort={sort} setSort={setSort} className="text-right" />
                      <SortHeader label="Data Termine" field="end_date" sort={sort} setSort={setSort} className="text-right" />
                      <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">Scadenza</th>
                      <th className="px-3 py-2.5 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(c => {
                      const discountAvg = getDiscountSummary(c);
                      const productNames = (c.line_items || []).map(li => li.product_name).filter(Boolean);
                      return (
                        <tr key={c.contract_id} className={`border-t border-border/40 hover:bg-muted/20 transition-colors ${selectedIds.has(c.contract_id) ? "bg-primary/5" : ""}`} data-testid={`contract-row-${c.contract_id}`}>
                          <td className="pl-3 py-3">
                            <input type="checkbox" className="h-4 w-4 cursor-pointer accent-primary" data-testid={`select-contract-${c.contract_id}`}
                              checked={selectedIds.has(c.contract_id)}
                              onChange={() => setSelectedIds(prev => {
                                const next = new Set(prev);
                                next.has(c.contract_id) ? next.delete(c.contract_id) : next.add(c.contract_id);
                                return next;
                              })} />
                          </td>
                          <td className="px-3 py-3">
                            <div>
                              <span className="font-medium">{c.client_name}</span>
                              {user?.role === "admin" && c.sales_rep_name && (
                                <p className="text-[11px] text-muted-foreground mt-0.5">{c.sales_rep_name}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[c.status]}`}>
                              {STATUS_LABELS[c.status]}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-muted-foreground">{c.sector}</td>
                          <td className="px-3 py-3">
                            <span className="text-xs px-1.5 py-0.5 bg-muted rounded">{c.sale_type}</span>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap gap-1">
                              {productNames.slice(0, 2).map((name, i) => (
                                <span key={i} className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100 truncate max-w-[120px]">{name}</span>
                              ))}
                              {productNames.length > 2 && (
                                <span className="text-xs text-muted-foreground">+{productNames.length - 2}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right">
                            {discountAvg ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-xs font-medium" data-testid={`discount-${c.contract_id}`}>-{discountAvg}%</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right font-medium">{fmtC(c.mrr)}</td>
                          <td className="px-3 py-3 text-right font-medium text-emerald-600">{fmtC(c.net_arr || c.arr)}</td>
                          <td className="px-3 py-3 text-right">{fmtC(c.net_contracted || c.total_contracted)}</td>
                          <td className="px-3 py-3 text-right text-muted-foreground text-xs">{new Date(c.created_at).toLocaleDateString("it-IT")}</td>
                          <td className="px-3 py-3 text-right">
                            {c.status !== "active" ? (
                              <EffectiveDateCell contract={c} onUpdate={loadData} />
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {c.expiration_date ? (() => {
                              const exp = new Date(c.expiration_date);
                              const now = new Date();
                              const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
                              const isExpiring = daysLeft <= 30 && daysLeft >= 0 && c.status === "active";
                              const isExpired = daysLeft < 0 && c.status === "active";
                              return (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${isExpired ? "bg-red-100 text-red-700 border border-red-200 font-medium" : isExpiring ? "bg-orange-100 text-orange-700 border border-orange-200 font-medium" : "text-muted-foreground"}`} data-testid={`expiration-${c.contract_id}`}>
                                  {exp.toLocaleDateString("it-IT")}
                                  {isExpired && " (scaduto)"}
                                  {isExpiring && ` (${daysLeft}gg)`}
                                </span>
                              );
                            })() : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-2 py-3">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingContract(c); setDialogOpen(true); }} data-testid={`edit-contract-${c.contract_id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-4 py-2 bg-muted/30 border-t border-border/40 text-xs text-muted-foreground">
                  {filtered.length} contratt{filtered.length === 1 ? "o" : "i"}{hasActiveFilters ? " (filtrati)" : ""}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Contract Dialog */}
      <ContractDialog
        open={dialogOpen}
        onClose={(v) => { setDialogOpen(v); if (!v) setEditingContract(null); }}
        products={products}
        clients={clients}
        salesReps={salesReps}
        contract={editingContract}
        onSave={handleSave}
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
        userRole={user?.role}
      />
    </div>
  );
}
